import {useEffect} from "react";
import {SubmitHandler} from "react-hook-form";
import {useQueryClient} from "react-query";
import {useNavigate, useParams} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import * as specificationApi from "../../api/equipmentApi/specificationApi";
import SubTitle from "../../components/SubTitle";
import {useGetItemQuery, useUpdateMutation} from "../../hooks/useQueryHooks";
import {ResponseItem} from "../../models/clientModels/interfaces/Response";
import * as Params from "../../models/clientModels/interfaces/apiParams";
import {
  EQSpecification,
  initialSpecification,
} from "../../models/serverModels/equipment/EQSpecification";
import EquipmentForm from "./EquipmentForm";

import {FormProvider, useForm} from "react-hook-form";
import {tokenSelector} from "../../Recoil/Atom/authState";
import {snackBarState, snackBarToggleState} from "../../Recoil/Atom/snackBarState";
import ConfirmButtonGroup from "../../components/ConfirmButtonGroup";
import MWProgress from "../../components/MWProgress";
import {FormSection} from "../../styledComponents/StyledCreateForm";
import {equipmentSchema} from "../../validation/equipmentSchema";
import {yupResolver} from "@hookform/resolvers/yup";

const UpdateEquipment = () => {
  const queryClient = useQueryClient();
  const {id} = useParams();
  const navigate = useNavigate();

  const tokenState = useRecoilValue(tokenSelector);

  const setSnackBarState = useSetRecoilState(snackBarState);
  const setSnackBarToggle = useSetRecoilState(snackBarToggleState);

  const {
    data: equipment,
    isLoading,
    isFetching,
  } = useGetItemQuery<ResponseItem<EQSpecification>>({
    key: ["Equipment", {id, token: tokenState}],
    api: () => specificationApi.getItem({id: parseInt(id!), token: tokenState}),
  });

  const {mutate: updateMutate} = useUpdateMutation<
    ResponseItem<EQSpecification>,
    Params.Update<EQSpecification>
  >({
    api: specificationApi.update,
    options: {
      onSuccess: () => {
        setSnackBarToggle(true);
        setSnackBarState("update");
        navigate("/Equipment");
        queryClient.invalidateQueries("EquipmentList");
      },
    },
  });

  const methods = useForm<EQSpecification>({
    defaultValues: equipment?.item || initialSpecification,
    resolver: yupResolver(equipmentSchema),
  });

  const {reset} = methods;

  useEffect(() => {
    reset(equipment?.item);
  }, [equipment?.item, reset]);

  const onSubmit: SubmitHandler<EQSpecification> = (data) => {
    updateMutate({
      id: parseInt(id!),
      token: tokenState,
      item: {...initialSpecification, ...data},
    });
  };

  return (
    <FormProvider {...methods}>
      <FormSection>
        {isLoading && isFetching ? (
          <MWProgress />
        ) : (
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <SubTitle subTitle="설비 제원 업데이트" />
            <EquipmentForm captionColor="#546E7A" />
            <ConfirmButtonGroup link="/Equipment" cancelText="취소" confirmText="수정" />
          </form>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default UpdateEquipment;
